import { useEffect, useRef } from 'react';
import type { ApprovalThreadEntry } from '@customedge/shared';

/**
 * Subscribes to real-time approval-chat comments for `designId` via SSE
 * (GET /api/events/design/:designId — see backend/src/services/
 * approvalEvents.ts). Mirrors usePrintStatus.ts's own EventSource lifecycle
 * pattern exactly (same connect-on-mount/close-on-unmount/auto-reconnect-on-
 * network-error shape), applied to the approval chat instead of print-job
 * status. `onComment` fires once per new ApprovalThreadEntry the backend
 * pushes — the caller decides how to fold it into its own thread state
 * (append, dedupe, sound/notification for the receiver, etc.), this hook
 * only owns the connection itself.
 */
export function useApprovalEvents(designId: string | null | undefined, onComment: (comment: ApprovalThreadEntry) => void): void {
  const onCommentRef = useRef(onComment);
  onCommentRef.current = onComment;

  useEffect(() => {
    if (!designId) return;

    const es = new EventSource(`/api/events/design/${encodeURIComponent(designId)}`);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'approval-comment' && data.comment) onCommentRef.current(data.comment);
      } catch { /* ignore malformed events, same as usePrintStatus.ts */ }
    };

    es.onerror = () => {
      // SSE auto-reconnects on network error; nothing to do here — same as
      // usePrintStatus.ts's own onerror (a design-approval thread can stay
      // open far longer than a print job, so giving up here would be worse).
    };

    return () => es.close();
  }, [designId]);
}
